import { useState, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';
import { STORAGE_KEYS } from '../config/cognito';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Only treat the app's sign-in callback as a Cognito OAuth response.
        // Other routes such as Google OAuth also use `code` params.
        const currentPath = window.location.pathname;
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        const isGoogleCallback = currentPath === '/oauth/google/callback';

        if (authCode && !isGoogleCallback) {
          console.log('OAuth callback detected, exchanging code for tokens...');
          try {
            await authService.handleOAuthCallback(authCode);

            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);

            const userAttributes = authService.getUserAttributes();
            if (userAttributes) {
              setUser(userAttributes);
              setIsAuthenticated(true);
              window.dispatchEvent(new CustomEvent('auth-state-changed'));
            }
            return;
          } catch (oauthError) {
            console.error('OAuth callback error:', oauthError);
            setError(oauthError.message || 'Failed to complete Google sign in');
            //clear code from URL even on error
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            return;
          } finally {
            setIsLoading(false);
          }
        }

        // Check if user is already authenticated
        let isAuthed = authService.isAuthenticated();

        // If not authenticated, try to refresh session (in case of expiry on load)
        if (!isAuthed) {
          console.log('Token expired or not found on load, attempting refresh...');
          isAuthed = await authService.checkAndRefreshSession();
        }

        if (isAuthed) {
          let userAttributes = authService.getUserAttributes();

          // If attributes not available, try to get from ID token
          if (!userAttributes || !userAttributes.email) {
            const tokens = authService.getStoredTokens();
            if (tokens.idToken) {
              const parsedToken = authService.parseJWTPayload(tokens.idToken);
              userAttributes = {
                email: parsedToken.email || parsedToken['cognito:username'],
                sub: parsedToken.sub,
                preferred_username: parsedToken['cognito:username'] || parsedToken.preferred_username,
                ...parsedToken,
                ...(userAttributes || {})
              };
              // Store the parsed attributes
              if (userAttributes.email) {
                localStorage.setItem(STORAGE_KEYS.USER_ATTRIBUTES, JSON.stringify(userAttributes));
              }
            }
          }

          if (userAttributes && userAttributes.email) {
            setUser(userAttributes);
            setIsAuthenticated(true);
            // Dispatch custom event to trigger permission reload
            window.dispatchEvent(new CustomEvent('auth-state-changed'));
          }
        }
      } catch (err) {
        setError(err.message || 'Error initializing authentication');
        console.error('Auth initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for storage changes to sync auth state across tabs
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEYS.ACCESS_TOKEN) {
        if (!e.newValue) {
          // Token removed, sign out
          setUser(null);
          setIsAuthenticated(false);
        } else {
          // Token added (login in another tab), re-initialize
          initializeAuth();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Sign Up
  const signUp = useCallback(async (username, email, password) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.signUp(username, email, password);

      return result;
    } catch (err) {
      setError(err.message || 'Error during sign up');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Confirm Sign Up
  const confirmSignUp = useCallback(async (username, confirmationCode) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.confirmSignUp(username, confirmationCode);

      return result;
    } catch (err) {
      setError(err.message || 'Error during confirmation');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sign In
  const signIn = useCallback(async (username, password) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.signIn(username, password);

      // Wait a bit to ensure attributes are stored, then refresh
      await new Promise(resolve => setTimeout(resolve, 100));

      // Update local state with fresh attributes
      const userAttributes = authService.getUserAttributes();
      if (userAttributes) {
        setUser(userAttributes);
        setIsAuthenticated(true);
      } else {
        // If attributes not available yet, try to get them from ID token
        const tokens = authService.getStoredTokens();
        if (tokens.idToken) {
          const parsedToken = authService.parseJWTPayload(tokens.idToken);
          const tokenUser = {
            email: parsedToken.email || parsedToken['cognito:username'],
            sub: parsedToken.sub,
            preferred_username: parsedToken['cognito:username'] || parsedToken.preferred_username,
            ...parsedToken
          };
          setUser(tokenUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }

      // Dispatch custom event to trigger permission reload
      window.dispatchEvent(new CustomEvent('auth-state-changed'));

      return result;
    } catch (err) {
      setError(err.message || 'Error during sign in');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sign Out
  const signOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.signOut();

      // Update local state
      setUser(null);
      setIsAuthenticated(false);

      // Force a browser refresh to fully reset app state
      window.location.reload();

      return result;
    } catch (err) {
      setError(err.message || 'Error during sign out');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Global Sign Out
  const globalSignOut = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.globalSignOut();

      // Update local state
      setUser(null);
      setIsAuthenticated(false);

      return result;
    } catch (err) {
      setError(err.message || 'Error during global sign out');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get Session
  const getSession = useCallback(async () => {
    try {
      setError(null);
      return await authService.getSession();
    } catch (err) {
      setError(err.message || 'Error getting session');
      throw err;
    }
  }, []);

  // Refresh Session
  const refreshSession = useCallback(async () => {
    try {
      setError(null);
      const result = await authService.refreshSession();

      // Update local state if refresh was successful
      if (result.session) {
        const userAttributes = authService.getUserAttributes();
        setUser(userAttributes);
        setIsAuthenticated(true);
      }

      return result;
    } catch (err) {
      setError(err.message || 'Error refreshing session');

      // If refresh fails, sign out the user
      setUser(null);
      setIsAuthenticated(false);

      throw err;
    }
  }, []);

  // Forgot Password
  const forgotPassword = useCallback(async (username) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.forgotPassword(username);

      return result;
    } catch (err) {
      setError(err.message || 'Error during forgot password');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Confirm Forgot Password
  const confirmForgotPassword = useCallback(async (username, confirmationCode, newPassword) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.confirmForgotPassword(username, confirmationCode, newPassword);

      return result;
    } catch (err) {
      setError(err.message || 'Error during password confirmation');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Change Password
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.changePassword(oldPassword, newPassword);

      return result;
    } catch (err) {
      setError(err.message || 'Error changing password');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Resend Confirmation Code
  const resendConfirmationCode = useCallback(async (username) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.resendConfirmationCode(username);

      return result;
    } catch (err) {
      setError(err.message || 'Error resending confirmation code');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // OAuth Sign In with Google
  const signInWithGoogle = useCallback(() => {
    try {
      setError(null);
      // This will redirect to Google OAuth
      authService.signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Error during Google sign in');
      throw err;
    }
  }, []);


  // OAuth Sign Up with Google  
  const signUpWithGoogle = useCallback(() => {
    try {
      setError(null);
      // This will redirect to Google OAuth
      authService.signUpWithGoogle();
    } catch (err) {
      setError(err.message || 'Error during Google sign up');
      throw err;
    }
  }, []);

  // Handle OAuth callback
  const handleOAuthCallback = useCallback(async (code) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.handleOAuthCallback(code);

      // Update local state
      const userAttributes = authService.getUserAttributes();
      setUser(userAttributes);
      setIsAuthenticated(true);

      return result;
    } catch (err) {
      setError(err.message || 'Error handling OAuth callback');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get stored tokens
  const getTokens = useCallback(() => {
    return authService.getStoredTokens();
  }, []);

  // Check if user has specific attribute
  const hasAttribute = useCallback((attributeName) => {
    return user && user[attributeName];
  }, [user]);

  // Get user attribute value
  const getAttribute = useCallback((attributeName) => {
    return user ? user[attributeName] : null;
  }, [user]);

  // Update user attributes
  const updateUserAttributes = useCallback(async (attributes) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await authService.updateUserAttributes(attributes);

      // Update local state with new attributes
      const updatedUser = { ...user, ...attributes };
      setUser(updatedUser);

      return result;
    } catch (err) {
      setError(err.message || 'Error updating user attributes');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Memoized values
  const authContextValue = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    error,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    getSession,
    refreshSession,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    resendConfirmationCode,
    clearError,
    getTokens,
    hasAttribute,
    getAttribute,
    updateUserAttributes,
    signInWithGoogle,
    signUpWithGoogle,
    handleOAuthCallback,
    globalSignOut,
  }), [
    user,
    isAuthenticated,
    isLoading,
    error,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    getSession,
    refreshSession,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    resendConfirmationCode,
    clearError,
    getTokens,
    hasAttribute,
    getAttribute,
    updateUserAttributes,
    signInWithGoogle,
    signUpWithGoogle,
    handleOAuthCallback,
    globalSignOut,
  ]);

  return authContextValue;
}
